import S, { DataSignal } from 's-js';
import SArray, { lift, SDataArray } from 's-array';
import { IAjax } from 'lib/Ajax';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { DraftMethods, DraftObject, mergeDraftMethods } from 'lib/draftSignals';
import { Monitor, Monitorable } from 'lib/Monitorable';
import { ValidationWatcher, filterErrors, mapErrorKeys } from 'lib/validationWatcher';

import { Berth } from 'data/models/Berth';
import { Coordination, CoordinationOptions } from 'data/models/Coordination';
import { CoordinationAltStatusLabel } from 'data/models/CoordinationAltStatusLabel';
import { CoordinationCardStatus } from 'data/models/CoordinationCardStatus';
import { CoordinationCurrency } from 'server/enums/CoordinationCurrency';
import { CoordinationEditingPolicy } from 'server/enums/CoordinationEditingPolicy';
import { CoordinationLocation } from 'data/models/CoordinationLocation';
import { CoordinationLocationType } from 'server/enums/CoordinationLocationType';
import { CoordinationMilestoneType } from 'data/models/CoordinationMilestoneType';
import { CoordinationProvider } from 'data/models/CoordinationProvider';
import { CoordinationProviderCurrency } from 'server/enums/CoordinationProviderCurrency';
import { Domain } from 'data/models/Domain';
import { Location } from 'data/models/Location';
import { MilestoneType } from 'data/models/MilestoneType';
import { Organization } from 'data/models/Organization';
import { PierGroup } from 'data/models/PierGroup';
import { Port } from 'data/models/Port';

import { PCCache } from 'data/PCCache';

import { Typeahead } from 'portcall/typeahead/model';

export interface CoordinationConfiguratorParams {
    /** The coordination Id, or undefined if configuring a new coordination. */
    id? : number;
}

export type CoordinationConfigurator = ReturnType<typeof CoordinationConfigurator>;
export function CoordinationConfigurator(
    loader : CachedModelLoader<Coordination, CoordinationOptions>,
    domain : Domain,
    ajax : IAjax,
    data : PCCache,
    params : CoordinationConfiguratorParams
) {
    // TODO: Better UI warnings about effects of actions like:
    // - Remove a milestone type
    // - Remove a main/related location
    // - Set to mandatory

    const
        initialDataLoad = params.id === undefined ? null : loader({ Id: params.id }),
        // Our persisted Coordination, if any. A signal so we can update it after a successful save.
        persisted = S.value(initialDataLoad?.model ?? null),
        upstream = S.sample(persisted) ?? new Coordination({
            Id: 0,
            Currency: CoordinationCurrency.Current,
            DisplayOrder: S.sample(() => {
                let max = -1;
                for (const coord of domain.Coordinations()) {
                    if (coord.DisplayOrder() > max) max = coord.DisplayOrder();
                }
                return max + 1;
            })
        }),
        { object: draft, methods: draftMethods } = makeDraftObject(upstream),
        url = () => (persisted() ? persisted()?.Url() : domain.CoordinationsUrl()) ?? '',
        // NOTE: DON'T use our data cache. The server isn't currently setup to return DataCacheUpdate responses
        // for either of these endpoints anyway, but even if it were, modifying coordinations can sometimes
        // add/remove a lot of C-Cards, and we don't wanna burden the server or the client w/ serializing and
        // deserializing every single one of them.
        client = ajax.Url<Coordination, CoordinationOptions>(url, {
            include: [
                c => c.AltStatusLabels,
                c => c.MilestoneTypes,
                c => c.Locations,
                c => c.Providers,
            ]
        }),
        ServerValidation = ValidationWatcher(client),
        CoordinationCardStatuses = lift(() => Array.from(data.CoordinationCardStatuses.items()))
            .map(status => CCardStatusViewModel(draft, status, ServerValidation))
            .orderBy(status => status.Model.DisplayOrder()),
        MilestoneTypes = lift(() => Array.from(data.MilestoneTypes.items()))
            .map(type => MilestoneTypeViewModel(draft, type))
            .orderBy(type => type.Model.DisplayOrder()),
        NumMilestoneTypesSelected = S(() => MilestoneTypes().filter(type => type.IsSelected()).length),
        allLocations = draft.Locations
            .map(cl => LocationViewModel(draft, cl)),
        MainLocations = allLocations
            .filter(l => l.Model.Type() === CoordinationLocationType.Main)
            .sort(LocationVMComparator),
        AddMainLocation = AddLocationViewModel(draft, CoordinationLocationType.Main, data),
        RelatedLocations = allLocations
            .filter(l => l.Model.Type() === CoordinationLocationType.Related)
            .sort(LocationVMComparator),
        AddRelatedLocation = AddLocationViewModel(draft, CoordinationLocationType.Related, data),
        addingNewProviders = S.value(false),
        Providers = draft.Providers
            .map(cp => ProviderViewModel(draft, cp, !S.sample(addingNewProviders)))
            .sort((cp1, cp2) => CoordinationProvider.DisplayOrder(cp1.Model, cp2.Model)),
        AddProvider = AddProviderViewModel(draft, data, addingNewProviders),
        // We'll handle most validation server-side (see ServerValidation declared above). But validating that
        // there's at least one current provider is handled more easily client-side, so we do that here.
        HasCurrentProvider = () => draft.Providers().some(p => p.Currency() === CoordinationProviderCurrency.Current),
        ClientValidationErrorsCount = () => {
            let count = 0;
            if (!HasCurrentProvider()) count++;
            return count;
        },
        SavingChanges = S.value(false);

    S(initialRun => {
        if (draft.IsMandatory() && !initialRun) draft.AgentPrompt(null);
        return false;
    }, true);

    // Clear main locations if not scoped by main location.
    S(initialRun => {
        if (!draft.IsScopedByMainLocation() && !initialRun) {
            // Go through each location VM's Delete method to honor the limitations of our draftArray util below.
            S.sample(MainLocations).forEach(l => l.Delete());
        }
        return false;
    }, true);

    // Clear related locations if not scoped by related location.
    S(initialRun => {
        if (!draft.IsScopedByRelatedLocation() && !initialRun) {
            // Go through each location VM's Delete method to honor the limitations of our draftArray util below.
            S.sample(RelatedLocations).forEach(l => l.Delete());
        }
        return false;
    }, true);

    // Whenever addingNewProviders is set to true, set it back to false on the next tick of S's clock.
    S.on(addingNewProviders, () => addingNewProviders(false));

    return {
        Loading: () => initialDataLoad?.loading() ?? false,
        Model: draft,
        ServerValidation,
        CoordinationCardStatuses,
        MilestoneTypes,
        NumMilestoneTypesSelected,
        MainLocations,
        AddMainLocation,
        RelatedLocations,
        AddRelatedLocation,
        Providers,
        AddProvider,
        OnSortProviders,
        HasCurrentProvider,
        ClientValidationErrorsCount,
        IsPersisted: () => !!persisted(),
        HasUnsavedChanges: () => !persisted() || draftMethods.isCaching(),
        SaveChanges,
        SavingChanges,
        QuitChanges
    };

    function makeDraftObject(upstream : Coordination) {
        return S.sample(() => {
            const
                { object: draft, methods } = DraftObject(upstream),
                allMethods = [methods];

            draft.AltStatusLabels = draftArray(upstream.AltStatusLabels, l => l.StatusId());
            draft.MilestoneTypes = draftArray(upstream.MilestoneTypes, t => t.MilestoneTypeId());
            draft.Locations = draftArray(upstream.Locations, l => `${l.LocationId()}:${l.Type()}`);
            draft.Providers = draftArray(upstream.Providers, p => p.ProviderId());

            return {
                object: draft,
                methods: mergeDraftMethods(...allMethods)
            };

            // TODO: Since writing this draftArray utility, I've added an attempt at an even more generic way
            // of doing the same thing. See PortCall.FrontEnd\lib\draftArray.ts. It might be worth trying to
            // switch to that version here. BUT BEWARE: The new DraftArray utility is NOT a drop-in replacement
            // for this one. The biggest difference is that this utility tries to be responsible for making
            // draft objects for each item in the original array, whereas the new DraftArray utility puts that
            // responsibility on calling code. PortCall.FrontEnd\apps\portcall\inportops\model.ts has examples
            // of how to use the new utility. But b/c those DraftArrays are for collections of one-to-many child
            // entities (cargo and cruise activities), and the ones here are for collections of many-to-many
            // child entities, there will have to be a few differences:
            // - This spot should use the MergeByKey function exported by PortCall.FrontEnd\lib\draftArray.ts
            //   instead of the MergeByObjectIdentity function.
            // - We need to handle cases like the following:
            //   - User defines a new AltStatusLabel (for example) at a point in time at which we believe the
            //     original AltStatusLabels array signal has no matching entry for that particular StatusId.
            //   - Later, the original AltStatusLabels array signal is updated so that it DOES now have a
            //     matching entry for that particular StatusId, but the matching entry is a DIFFERENT
            //     CoordinationAltStatusLabel object. B/c we'll use MergeByKey, we'll correctly swap in the new
            //     "official" CoordinationAltStatusLabel object in place of the one we added when we thought
            //     there was no such official object. Cool.
            //   - But we'll still have a draft CoordinationAltStatusLabel object that references our TEMPORARY
            //     CoordinationAltStatusLabel object as its upstream data source. To avoid throwing away those
            //     pending user edits, when making a new draft object for the new OFFICIAL
            //     CoordinationAltStatusLabel object, we should make sure to detect that we'd already made a
            //     draft object for the same StatusId and copy that old draft object's payload fields over to
            //     the new draft object before we forget about the old draft object.
            //   - The same concern comes up for all the draft arrays here, not just AltStatusLabels.
            // Anyhow, skipping the above for now.
            //
            // Original comment:
            // Construct an array that mirrors an upstream/original array signal except that it holds draft
            // objects instead of committed model objects and can have objects added to/removed from it w/o
            // affecting the upstream array signal. While this is fairly generic, it's NOT intended as a
            // general solution for making "draft" signals that implement the SArray.ArrayDataSignal interface.
            // In particular, this implementation makes the following assumptions:
            //  - It assumes the order of elements in the resulting draft array signal is irrelevant.
            //  - It assumes that objects will only be added to or removed from the resulting draft array signal
            //    via its push and remove methods.
            //  - Each array signal's elements can be uniquely indexed via the provided key func.
            // All three assumptions are safe in our case.
            function draftArray<T extends object, K>(original : () => T[], keyFunc : (obj : T) => K) {
                let version = 0;

                const
                    onReinit = S.data(null),
                    isCaching = S.value(false),
                    drafts = Monitorable<SDataArray<T>, SArrayMonitor<T>>(SArray<T>([])),
                    methodsCache = new Map<T, DraftMethods>(),
                    pushed = new Set<K>(),
                    removed = new Set<K>(),
                    logWrite = () => (version++, isCaching(true)),
                    monitor : SArrayMonitor<T> = {
                        push(item) {
                            const key = keyFunc(item);
                            pushed.add(key);
                            removed.delete(key);
                            logWrite();
                        },
                        remove(item) {
                            methodsCache.delete(item);
                            const key = keyFunc(item);
                            pushed.delete(key);
                            removed.add(key);
                            logWrite();
                        }
                    };

                Monitor(drafts, monitor);

                // Add matching draft objects as new objects are added to the upstream array, but only so long
                // as we haven't already made a draft object for the upstream object and haven't explicitly
                // removed a matching draft object from the downstream array.
                S(() => {
                    onReinit();

                    const draftsByKey = new Map<K, T>();

                    for (const draft of S.sample(drafts)) {
                        draftsByKey.set(keyFunc(draft), draft);
                    }

                    const newDrafts : T[] = [];
                    for (const obj of original()) {
                        const key = keyFunc(obj);
                        // Skip if explicitly removed, and also skip if explicitly pushed b/c we'll do those
                        // below.
                        if (!removed.has(key) && !pushed.has(key)) {
                            let draft = draftsByKey.get(key);
                            if (!draft) {
                                const { object, methods } = DraftObject(obj);
                                methodsCache.set(object, methods);
                                draft = object;
                            }
                            newDrafts.push(draft);
                        }
                    }
                    pushed.forEach(key => newDrafts.push(draftsByKey.get(key)!));
                    drafts(newDrafts);
                });

                allMethods.push({
                    isCaching: () => isCaching() || drafts().some(d => methodsCache.get(d)?.isCaching() ?? false),
                    // onWrite would be kinda annoying to support b/c really we should be catching writes to
                    // new objects pushed by downstream code in addition to draft objects constructed above.
                    // But we don't ever need this onWrite anyway.
                    onWrite: () => { throw new Error("Not implementing onWrite for draftArray.") },
                    checkpoint: () => {
                        const checkpointVersion = version;
                        return {
                            commit() {
                                // Committing would be kinda annoying to support b/c really we should
                                // preserve object identity for objects that already exist in the upstream
                                // array. But we don't ever need to commit anyway.
                                throw new Error("Not implementing commit for draftArray.");
                            },
                            revert() {
                                if (checkpointVersion === version) {
                                    // Reverting just means re-initing from the upstream array.
                                    // FIXME: There's a hole here: OK, so we'll re-init from the usptream
                                    // array. But what if some child item has been written to since when
                                    // this checkpoint was taken? As is, we'll throw away those edits
                                    // unless we've ALSO added or removed child items since this
                                    // checkpoint was taken. Basically, our version counter doesn't count
                                    // writes to child items, only push or remove calls to this draft
                                    // array. Since the current UX is that we return to the coordinations
                                    // overview list after a successful save, this doesn't matter in
                                    // practice for now, as we're gonna lose any user edits made while the
                                    // save is in progress anyway. But if we switch to letting users stay
                                    // on this configurator page after a successful save, then we might
                                    // wanna tackle this one; the current behavior would be that we might
                                    // throw away edits to child items that the user made while the save
                                    // was in progress. We should probably do that in combo w/ switching to
                                    // the newer DraftArray utility from PortCall.FrontEnd\lib\draftArray.ts.
                                    // See TODO comment above.
                                    S.freeze(() => {
                                        onReinit(null);
                                        isCaching(false);
                                        drafts([]);
                                        methodsCache.clear();
                                        pushed.clear();
                                        removed.clear();
                                    });
                                }
                            }
                        };
                    }
                });

                return drafts;

                interface SArrayMonitor<T> {
                    push? : (item : T) => void;
                    remove? : (item : T) => void;
                }
            }
        });
    }

    function OnSortProviders(_movedProvider : ProviderViewModel, newOrder : ProviderViewModel[]) {
        S.freeze(() => newOrder.forEach((p, i) => p.Model.DisplayOrder(i)));
    }

    function SaveChanges() {
        // TODO: A nice-to-have here would be diffing to see if the save is likely to require adding/removing C-Cards
        // (i.e. the save will trigger
        // PortCall.Services\CoordinationCards\CoordinationConfigurationChanged_SyncCCards.cs), and if so spawn a
        // confirmation popup to make sure admins realize what they're doing.
        if (SavingChanges()) return;
        SavingChanges(true);
        const
            req = persisted() ? client.Put(draft) : client.Post(draft),
            checkpoint = draftMethods.checkpoint();
        req.then(coordData => {
            S.freeze(() => {
                // Use our upstream Coordination as a template such that if this is a new Coordination that we
                // just saved, we'll attach it to our PCCache and reverting below will pick up the values
                // persisted from the server.
                data.Coordinations.update1(req.Date, coordData, upstream);
                // Record that we have a persisted Coordination now.
                persisted(upstream);
                // Revert draft changes to pick up the updated values from our PCCache. This is future proofing
                // in case we ever decide we want users to stay on this page after saving to be able to make
                // further edits. For now, redundant b/c we're gonna navigate to the coordinations overview
                // page next and thus throw away this page's in memory state anyway.
                checkpoint.revert();
            });
            QuitChanges();
        }).finally(() => SavingChanges(false));
    }

    function QuitChanges() {
        window.location.href = '#!coordinationsoverview';
    }
}

export type CCardStatusViewModel = ReturnType<typeof CCardStatusViewModel>;
function CCardStatusViewModel(
    coordination : Coordination,
    status : CoordinationCardStatus,
    serverValidation : ValidationWatcher
) {
    const
        altLabelIndex = S(() => coordination.AltStatusLabels().findIndex(l => l.Status() === status)),
        altLabel = () => altLabelIndex() >= 0 ? coordination.AltStatusLabels()[altLabelIndex()] : null,
        AltLabelText : DataSignal<string> = (val? : string) => {
            if (val === undefined) {
                return altLabel()?.Label() ?? '';
            } else {
                S.sample(() => S.freeze(() => {
                    let l = altLabel();
                    if (val) {
                        if (!l) {
                            l = new CoordinationAltStatusLabel({
                                CoordinationId: coordination.Id(),
                                StatusId: status.Id()
                            });
                            l.Status(status);
                            coordination.AltStatusLabels.push(l);
                        }
                        l.Label(val);
                    } else if (l) {
                        coordination.AltStatusLabels.remove(l);
                        // We're removing an object from our AltStatusLabels array, so if we did nothing to our
                        // serverValidation collection, that'd cause our matching of validation errors to
                        // CCardStatusViewModels to get off for all CCardStatusViewModels that happened to have
                        // a higher altLabelIndex than this one. For an easy fix, we'll just throw away any
                        // validation error related to our AltStatusLabels array.
                        serverValidation(filterErrors(serverValidation(), key => key.indexOf('AltStatusLabels') !== 0));
                    }
                }));
                return val;
            }
        },
        validationPrefix = () => `AltStatusLabels[${altLabelIndex()}].`,
        filteredValidation = () => mapErrorKeys(
            filterErrors(serverValidation(), key => key.indexOf(validationPrefix()) === 0),
            key => key.substring(validationPrefix().length)
        ),
        IsEditingLabel = S.value(false);

    return {
        Model: status,
        AltLabelText,
        ServerValidation: filteredValidation,
        IsEditingLabel
    };
}

export type MilestoneTypeViewModel = ReturnType<typeof MilestoneTypeViewModel>;
function MilestoneTypeViewModel(
    coordination : Coordination,
    type : MilestoneType
) {
    const
        coordType = S(() => coordination.MilestoneTypes().find(t => t.MilestoneType() === type) ?? null),
        IsSelected : DataSignal<boolean> = (val? : boolean) => {
            if (val === undefined) {
                return !!coordType();
            } else {
                S.sample(() => S.freeze(() => {
                    let t = coordType();
                    if (val && !t) {
                        t = new CoordinationMilestoneType({
                            CoordinationId: coordination.Id(),
                            MilestoneTypeId: type.Id()
                        });
                        t.MilestoneType(type);
                        coordination.MilestoneTypes.push(t);
                    } else if (t && !val) {
                        coordination.MilestoneTypes.remove(t);
                    }
                }));
                return val;
            }
        };
    return { Model: type, IsSelected };
}

export type LocationViewModel = ReturnType<typeof LocationViewModel>;
function LocationViewModel(
    coordination : Coordination,
    cl : CoordinationLocation
) {
    return { Model: cl, Delete };

    function Delete() {
        coordination.Locations.remove(cl);
    }
}

function LocationVMComparator(cl1 : LocationViewModel, cl2 : LocationViewModel) {
    // Order locations as we do on the berth timeline, i.e. by Port, then by PierGroup, then by Berth.
    const
        l1 = cl1.Model.Location(),
        p1 = l1?.Port(),
        b1 = l1?.Berth(),
        pg1 = b1?.PierGroup(),
        l2 = cl2.Model.Location(),
        p2 = l2?.Port(),
        b2 = l2?.Berth(),
        pg2 = b2?.PierGroup();

    return (
        p1 ?
            p2 ?
                p2 === p1 ?
                    b1 ?
                        b2 ?
                            pg1 ?
                                pg2 ?
                                    pg2 === pg1 ?
                                        Berth.StartMarkOrder(b1, b2) :
                                        PierGroupComparator(pg1, pg2) :
                                    -1 /* sort berths w/ missing piergroup last among berths for that port */ :
                                pg2 ?
                                    1 /* sort berths w/ missing piergroup last among berths for that port */ :
                                    0 :
                            1 /* sort locations w/ no berth (like port arrival/departure points) ahead of berths */ :
                        b2 ?
                            -1 /* sort locations w/ no berth (like port arrival/departure points) ahead of berths */ :
                            // We've already confirmed p1 and p2 are both truthy, which means l1 and l2 HAVE to
                            // be truthy too. Typescript's nullability analysis just doesn't seem to get that.
                            BerthlessLocationComparator(l1!, l2!) :
                    PortComparator(p1, p2) :
                -1 /* sort locations w/ missing Port last */ :
            p2 ?
                1 /* sort locations w/ missing Port last */ :
                0
    );
}

// Compare Locations that are in the same Port and don't have berths.
function BerthlessLocationComparator(l1 : Location, l2 : Location) {
    // Neither has a berth. Sort by name case-insensitively, I guess?
    const name1 = l1.Name().toLowerCase(), name2 = l2.Name().toLowerCase();
    return name1 < name2 ? -1 : name1 > name2 ? 1 : 0;
}

// Compare PierGroups that are in the same Port.
function PierGroupComparator(pg1 : PierGroup, pg2 : PierGroup) {
    const diff = pg1.Order() - pg2.Order();
    // Sort by Id as a fallback if orders are the same
    return diff === 0 ? pg1.Id() - pg2.Id() : diff;
}

// Compare Ports that are in the same Domain.
function PortComparator(p1 : Port, p2 : Port) {
    const diff = p1.Order() - p2.Order();
    // Sort by Id as a fallback if orders are the same
    return diff === 0 ? p1.Id() - p2.Id() : diff;
}

export type AddLocationViewModel = ReturnType<typeof AddLocationViewModel>;
function AddLocationViewModel(
    coordination : Coordination,
    type : CoordinationLocationType,
    data : PCCache
) {
    const
        selectedLocations = S(() => {
            const sel = new Set<Location>();
            for (const cl of coordination.Locations()) {
                if (cl.Type() === type && cl.Location()) sel.add(cl.Location()!);
            }
            return sel;
        }),
        availableLocations = S(() => {
            const sel = selectedLocations();
            return Array.from(data.Locations.items()).filter(l => !sel.has(l));
        }),
        locationToAdd = S.value<Location | null>(null),
        locationQuery = S.value(''),
        LocationSelector = new Typeahead<Location>({
            collection: availableLocations,
            matchFn: (l, query) => CaseInsensitiveContains(LocationDisplayKey(l), query),
            typeaheadOptions: {
                minLength: 0,
                highlight: true,
                hint: false
            },
            observable: locationQuery,
            displayKey: LocationDisplayKey,
            onSelect: l => locationToAdd(l)
        });

    S.on(locationToAdd, () => locationQuery(locationToAdd() ? LocationDisplayKey(locationToAdd()!) : ''));
    S.on(locationToAdd, () => {
        const l = locationToAdd();
        if (l) {
            S.freeze(() => {
                const cl = new CoordinationLocation({
                    CoordinationId: coordination.Id(),
                    LocationId: l.Id(),
                    Type: type
                });
                cl.Location(l);
                coordination.Locations.push(cl);
                // Clear our typeahead for faster entry of the next location, if any.
                locationToAdd(null);
            });
        }
    });

    return { LocationSelector };
}

export function LocationDisplayKey(l : Location) {
    return `${l.Name()} (${l.Port()?.Name() ?? 'loading...'})`;
}

export type ProviderViewModel = ReturnType<typeof ProviderViewModel>;
function ProviderViewModel(
    coordination : Coordination,
    provider : CoordinationProvider,
    IsPersisted : boolean
) {
    return {
        Model: provider,
        IsPersisted,
        GetEditingPolicyBits,
        SetEditingPolicyBits,
        Delete
    };

    /**
     * Get the provider's EditingPolicy for the bits indicated by the provided mask.
     */
    function GetEditingPolicyBits(mask : CoordinationEditingPolicy) {
        return provider.EditingPolicy() & mask;
    }

    /**
     * Set the provider's EditingPolicy for the bits indicated by the provided mask using the bit values from
     * the provided value.
     */
    function SetEditingPolicyBits(mask : CoordinationEditingPolicy, value : CoordinationEditingPolicy) {
        // NOTE: Use of JS bitwise NOT (~) operator assumes CoordinationEditingPolicy enum stays w/in the range
        // of a 32-bit integer, which it currently does.
        // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Bitwise_NOT
        provider.EditingPolicy(S.sample(provider.EditingPolicy) & ~mask | value & mask);
    }

    function Delete() {
        // The server would technically let us delete a persisted provider so long as it doesn't have any
        // existing C-Cards. But trying to delete a provider w/ existing C-Cards would throw a 500 error.
        // So for now, we'll be conservative and say you can't delete persisted providers at all. Deprecating
        // achieves more or less the same thing except that the provider is still visible on this configurator
        // page.
        if (IsPersisted) throw new Error("Can't delete persisted coordination providers. Deprecate instead.");
        coordination.Providers.remove(provider);
    }
}

export type AddProviderViewModel = ReturnType<typeof AddProviderViewModel>;
function AddProviderViewModel(
    coordination : Coordination,
    data : PCCache,
    addingNewProviders : DataSignal<boolean>
) {
    const
        selectedProviders = S(() => {
            const sel = new Set<Organization>();
            for (const cp of coordination.Providers()) {
                if (cp.Provider()) sel.add(cp.Provider()!);
            }
            return sel;
        }),
        availableProviders = S(() => {
            const sel = selectedProviders();
            return Array.from(data.Organizations.items()).filter(b => !sel.has(b));
        }),
        providerToAdd = S.value<Organization | null>(null),
        providerQuery = S.value(''),
        ProviderSelector = new Typeahead<Organization>({
            collection: availableProviders,
            matchFn: (org, query) => CaseInsensitiveContains(ProviderDisplayKey(org), query),
            typeaheadOptions: {
                minLength: 0,
                highlight: true,
                hint: false
            },
            observable: providerQuery,
            displayKey: ProviderDisplayKey,
            onSelect: org => providerToAdd(org)
        });

    S.on(providerToAdd, () => providerQuery(providerToAdd() ? ProviderDisplayKey(providerToAdd()!) : ''));
    S.on(providerToAdd, () => {
        const p = providerToAdd();
        if (p) {
            S.freeze(() => {
                let maxDisplayOrder = -1;
                for (const otherCP of coordination.Providers()) {
                    if (otherCP.DisplayOrder() > maxDisplayOrder) maxDisplayOrder = otherCP.DisplayOrder();
                }
                const cp = new CoordinationProvider({
                    CoordinationId: coordination.Id(),
                    ProviderId: p.Id(),
                    DisplayOrder: maxDisplayOrder + 1,
                    // Default to the more permissive option and admins can opt into the more restrictive behavior.
                    EditingPolicy: CoordinationEditingPolicy.AllowAgentStatusEdits
                });
                cp.Provider(p);
                addingNewProviders(true);
                coordination.Providers.push(cp);
                // Clear our typeahead for faster entry of the next provider, if any.
                providerToAdd(null);
            });
        }
    });

    return { ProviderSelector };
}

function ProviderDisplayKey(provider : Organization) {
    return provider.Name();
}



// WEBPACK FOOTER //
// ./apps/portcall/coordinationconfigurator/model.ts